import { SaleAngle } from "../services/interface";
import { UploadedMethodType } from "../store/onboardingStore";

export type Variant = {
  name: string;
  variant_key: string;
  price: number;
  available: boolean;
  images: string[];
  attributes: any[];
  provider_id?: string;
  external_id?: string;
  external_sell_price?: number;
  external_suggested_sell_price?: number;
};

export type ProductData = {
  name: string;
  price: string;
  details: string;
  warranty: "none" | "30days" | "90days" | "custom";
  freeShipping: boolean;
  isManualEntry: boolean;
  productLink: string;
  customWarrantyDays: string;
  externalId?: string | null;
  providerId?: string | null;
  uploadedMethod: UploadedMethodType;
  variants: Variant[] | null;
};

export type VisualContent = {
  images: string[];
  imagesBase64: string[];
  selectedAngle: SaleAngle[];
  customAngle?: string;
};

export type AdditionalOffers = {
  upsell: {
    enabled: boolean;
    products: Array<{
      id: string;
      productName: string;
      price: string;
      description: string;
      url_images?: string[];
      image?: string;
      isManualEntry: boolean;
      productLink: string;
      externalId?: string | null;
      providerId?: string | null;
      uploadedMethod?: UploadedMethodType;
    }>;
    isManualEntry?: boolean;
    productLink?: string;
    productName?: string;
    price?: string;
    description?: string;
    image?: string;
    url_images?: string[];
  };
  downsell: {
    enabled: boolean;
    discountPercentage: string;
  };
  bumpOffer?: {
    enabled: boolean;
    productName: string;
    price: string;
    description?: string;
    url_images?: string;
    image?: string;
    externalId?: string | null;
    providerId?: string | null;
    isManualEntry: boolean;
    productLink?: string | null;
    uploadedMethod?: UploadedMethodType;
  };
};

export type VisualStyle = {
  colorPalette:
    | ""
    | "professional-blue"
    | "natural-green"
    | "creative-purple"
    | "warm-sunset"
    | "minimal-gray"
    | "royal-gold"
    | "custom";
  brandName?: string;
  logoImage?: string;
  useAiLogo?: boolean;
};

export type OnboardingData = {
  step: number;
  productData: ProductData;
  visualContent: VisualContent;
  additionalOffers: AdditionalOffers;
  visualStyle: VisualStyle;
  isManualEntry: boolean;
  productLink: string;
};

export type SalesAngle = {
  id: string;
  title: string;
  description: string;
  icon?: React.ComponentType<any>;
  isRecommended?: boolean;
};

// Define ContextData type
export type ContextData = {
  pain_detection: string;
  buyer_detection: string;
  recommended_sales_angles: any[]; // O un tipo más específico si lo tienes
};

// Multi-funnel flow types
export type FunnelSection = {
  id: string;
  name: string;
  icon: string;
  enabled: boolean;
  order: number;
  sectionType?: string;   // API section_type (e.g., "portada", "beneficios-destacados")
  url?: string;           // Preview image URL from API
  isStandard?: boolean;   // true for structural sections (FAQ, CTA, Guarantee)
};

export type FunnelConfig = {
  id: string;
  name: string;
  designTemplate: string | null;       // Template ID (for preview display)
  designTemplateType: string | null;   // Template type slug (e.g., "tecnologia-neon-verde")
  sections: FunnelSection[];
  selectedAngles: { name: string; description: string }[];
  selectedAdTemplates: string[];       // Array of AdStyle.id values for ad creative templates
};

export type MultiFunnelData = {
  funnelCount: number;
  funnels: FunnelConfig[];
};

// Legacy (code-based) templates — these use the old POST /websites endpoint
export type LegacyTemplate = {
  id: string;
  name: string;
  description: string;
  styleSheet: string;
  palette: string;
  coverStyle: string;
  previewUrl: string;
};

export const LEGACY_TEMPLATES: LegacyTemplate[] = [
  {
    id: 'legacy-mascotas-limpieza-dental-verde',
    name: 'Mascotas-Limpieza-Dental-Verde',
    description: 'Landing de producto de limpieza dental para mascotas con estética natural/orgánica verde',
    styleSheet: 'vital_green',
    palette: 'vital_green',
    coverStyle: 'above_the_fold_principal',
    previewUrl: 'https://fluxi.s3.amazonaws.com/dev/assets/sections/personalized/templates/Preview-Mascotas-Limpieza-Dental-Verde',
  },
  {
    id: 'legacy-deporte-hoodie-rojo',
    name: 'Deporte-Hoodie-Rojo',
    description: 'Landing de hoodie deportivo con estética pop roja intensa',
    styleSheet: 'premium_fashion',
    palette: 'girly_pink',
    coverStyle: 'above_the_fold_principal',
    previewUrl: 'https://fluxi.s3.amazonaws.com/dev/assets/sections/personalized/templates/Preview-Deporte-Hoodie-Rojo',
  },
  {
    id: 'legacy-salud-vitaminas-ninos-naranja',
    name: 'Salud-Vitaminas-Ninos-Naranja',
    description: 'Landing de vitaminas para niños con estética cálida naranja',
    styleSheet: 'modern_medical',
    palette: 'warm_orange',
    coverStyle: 'above_the_fold_principal',
    previewUrl: 'https://fluxi.s3.amazonaws.com/dev/assets/sections/personalized/templates/Preview-Salud-Vitaminas-Ninos-Naranja',
  },
  {
    id: 'legacy-tecnologia-gopro-azul',
    name: 'Tecnologia-GoPro-Azul',
    description: 'Landing de cámara GoPro con estética oscura/azul premium',
    styleSheet: 'tech_showcase',
    palette: 'modern_medical',
    coverStyle: 'above_the_fold_principal',
    previewUrl: 'https://fluxi.s3.amazonaws.com/dev/assets/sections/personalized/templates/Preview-Tecnologia-GoPro-Azul',
  },
  {
    id: 'legacy-belleza-labial-gloss-rosa',
    name: 'Belleza-Labial-Gloss-Rosa',
    description: 'Landing de labial/gloss con estética femenina rosa moderna',
    styleSheet: 'girly_pink',
    palette: 'sophisticated_feminine',
    coverStyle: 'above_the_fold_principal',
    previewUrl: 'https://fluxi.s3.amazonaws.com/dev/assets/sections/personalized/templates/Preview-Belleza-Labial-Gloss-Rosa',
  },
];
